import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
  useRef
} from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useCssHandles } from '../../../shared/hooks/useCssHandles'
import { AdminLayout } from '../components'
import { Modal, ConfirmModal, Select, Input, Checkbox, Textarea } from '../../../shared/components'
import { adminCouponService } from '../../../services'
import { useToast } from '../../../contexts/ToastContext'
import {
  AdminCoupon,
  AdminCouponDetail,
  AdminCouponPayload,
  AdminCouponUsage
} from '../../../types/adminCoupons'

type Coupon = AdminCoupon
type CouponUsageLog = AdminCouponUsage

const CSS_HANDLES = [
  'adminCoupons',
  'couponsHeader',
  'couponsTitle',
  'couponsSubtitle',
  'couponsActionButton',
  'couponsStats',
  'couponsStatCard',
  'couponsStatValue',
  'couponsStatLabel',
  'couponsTableWrapper',
  'couponsTable',
  'tableHeader',
  'tableRow',
  'couponCode',
  'couponDescription',
  'couponTypePill',
  'couponValue',
  'couponUsage',
  'couponStatus',
  'couponActions',
  'actionButton',
  'viewModal',
  'viewModalContent',
  'viewModalSection',
  'viewModalHeader',
  'viewModalSummaryGrid',
  'viewModalActions',
  'usageLogContainer',
  'usageLogHeader',
  'usageLogTable',
  'usageLogEmpty',
  'modal',
  'modalOverlay',
  'modalContent',
  'modalHeader',
  'modalTitle',
  'modalClose',
  'modalBody',
  'formRow',
  'formGroup',
  'formLabel',
  'formInput',
  'formSelect',
  'formTextarea',
  'modalFooter',
  'cancelButton',
  'saveButton'
] as const

type CouponFormValues = Pick<
  Coupon,
  | 'code'
  | 'type'
  | 'value'
  | 'minOrderValue'
  | 'maxUses'
  | 'validFrom'
  | 'validUntil'
  | 'isActive'
  | 'description'
>

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)

const formatDate = (value?: string) => {
  if (!value) {
    return '—'
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return '—'
  }

  return parsed.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  })
}

const resolveErrorMessage = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message : fallback

const formatDateInputValue = (value?: string) =>
  value ? value.substring(0, 10) : ''

const AdminCoupons: React.FC = () => {
  const handles = useCssHandles(CSS_HANDLES)
  const { addToast } = useToast()
  const [coupons, setCoupons] = useState<Coupon[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isDetailLoading, setIsDetailLoading] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null)
  const [viewingCoupon, setViewingCoupon] = useState<AdminCouponDetail | null>(null)

  // Ref para evitar chamadas duplicadas
  const hasLoadedCoupons = useRef(false)
  const isLoadingRef = useRef(false)

  const loadCoupons = useCallback(async () => {
    // Evitar execuções duplicadas (especialmente no React Strict Mode)
    if (hasLoadedCoupons.current && !isLoadingRef.current) {
      return
    }

    // Verificar se já está carregando para evitar chamadas simultâneas
    if (isLoadingRef.current) {
      return
    }

    hasLoadedCoupons.current = true
    isLoadingRef.current = true

    try {
      setIsLoading(true)
      const data = await adminCouponService.list()
      setCoupons(data)
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Erro ao carregar cupons',
        message: resolveErrorMessage(error, 'Não foi possível carregar os cupons. Tente novamente.')
      })
      // Resetar flag em caso de erro para permitir nova tentativa
      hasLoadedCoupons.current = false
    } finally {
      setIsLoading(false)
      isLoadingRef.current = false
    }
  }, [addToast])

  useEffect(() => {
    void loadCoupons()
  }, [loadCoupons])

  const stats = useMemo(
    () => ({
      total: coupons.length,
      active: coupons.filter((coupon) => coupon.isActive).length,
      usages: coupons.reduce((total, coupon) => total + coupon.usedCount, 0)
    }),
    [coupons]
  )

  const openEditModal = (coupon: Coupon | null) => {
    setEditingCoupon(coupon)
    setIsEditModalOpen(true)
  }

  const handleCreateCoupon = () => {
    openEditModal(null)
  }

  const handleEditCoupon = (coupon: Coupon) => {
    openEditModal(coupon)
  }

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false)
    setEditingCoupon(null)
  }

  const mapFormValuesToPayload = (values: CouponFormValues): AdminCouponPayload => ({
    code: values.code,
    type: values.type,
    value: values.value,
    minOrderValue: values.minOrderValue ?? null,
    maxDiscount: undefined,
    maxUses: values.maxUses ?? null,
    validFrom: values.validFrom,
    validUntil: values.validUntil,
    isActive: values.isActive,
    description: values.description || undefined
  })

  const handleSaveCoupon = async (values: CouponFormValues) => {
    try {
      setIsSaving(true)
      const payload = mapFormValuesToPayload(values)
      let savedCoupon: Coupon

      if (editingCoupon) {
        savedCoupon = await adminCouponService.update(editingCoupon.id, payload)
        setCoupons((previous) =>
          previous.map((coupon) => (coupon.id === savedCoupon.id ? savedCoupon : coupon))
        )
        setViewingCoupon((previous) =>
          previous && previous.coupon.id === savedCoupon.id
            ? { coupon: savedCoupon, usageLog: previous.usageLog }
            : previous
        )
        addToast({
          type: 'success',
          title: 'Cupom atualizado',
          message: `As alterações do cupom ${savedCoupon.code} foram salvas.`
        })
      } else {
        savedCoupon = await adminCouponService.create(payload)
        setCoupons((previous) => [savedCoupon, ...previous])
        addToast({
          type: 'success',
          title: 'Cupom criado',
          message: `O cupom ${savedCoupon.code} foi cadastrado com sucesso.`
        })
      }

      handleCloseEditModal()
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Erro ao salvar cupom',
        message: resolveErrorMessage(error, 'Não foi possível salvar o cupom. Tente novamente.')
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleViewCoupon = async (coupon: Coupon) => {
    setViewingCoupon({
      coupon,
      usageLog: []
    })

    try {
      setIsDetailLoading(true)
      const detail = await adminCouponService.getById(coupon.id)
      setViewingCoupon(detail)
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Erro ao carregar detalhes',
        message: resolveErrorMessage(error, 'Não foi possível carregar os detalhes do cupom.')
      })
    } finally {
      setIsDetailLoading(false)
    }
  }

  const handleCloseViewCoupon = () => {
    setViewingCoupon(null)
    setIsDetailLoading(false)
  }

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [couponToDelete, setCouponToDelete] = useState<AdminCoupon | null>(null)

  const handleDeleteCoupon = (coupon: AdminCoupon) => {
    setCouponToDelete(coupon)
    setDeleteConfirmOpen(true)
  }

  const confirmDeleteCoupon = async () => {
    if (!couponToDelete) return

    try {
      await adminCouponService.remove(couponToDelete.id)
      setCoupons((previous) => previous.filter((coupon) => coupon.id !== couponToDelete.id))
      setViewingCoupon((previous) =>
        previous && previous.coupon.id === couponToDelete.id ? null : previous
      )
      if (editingCoupon?.id === couponToDelete.id) {
        handleCloseEditModal()
      }
      addToast({
        type: 'success',
        title: 'Cupom excluído',
        message: 'O cupom foi removido com sucesso.'
      })
      setDeleteConfirmOpen(false)
      setCouponToDelete(null)
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Erro ao excluir cupom',
        message: resolveErrorMessage(error, 'Não foi possível excluir o cupom.')
      })
      setDeleteConfirmOpen(false)
      setCouponToDelete(null)
    }
  }

  const handleToggleCouponStatus = async (coupon: Coupon) => {
    try {
      const updated = await adminCouponService.updateStatus(coupon.id, !coupon.isActive)
      setCoupons((previous) =>
        previous.map((item) => (item.id === updated.id ? updated : item))
      )
      setViewingCoupon((previous) =>
        previous && previous.coupon.id === updated.id
          ? { coupon: updated, usageLog: previous.usageLog }
          : previous
      )
      addToast({
        type: 'success',
        title: updated.isActive ? 'Cupom ativado' : 'Cupom desativado',
        message: `O cupom ${updated.code} foi ${updated.isActive ? 'ativado' : 'desativado'}.`
      })
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Erro ao atualizar status',
        message: resolveErrorMessage(error, 'Não foi possível atualizar o status do cupom.')
      })
    }
  }

  const getTypeConfig = (type: Coupon['type']) => {
    switch (type) {
      case 'percentage':
        return { label: 'Percentual', className: 'bg-blue-100 text-blue-800' }
      case 'fixed':
        return { label: 'Valor Fixo', className: 'bg-green-100 text-green-800' }
      case 'free_shipping':
        return { label: 'Frete Grátis', className: 'bg-yellow-100 text-yellow-800' }
      default:
        return { label: type, className: 'bg-gray-100 text-gray-700' }
    }
  }

  const actionButtonClassName = `${handles.actionButton} inline-flex h-9 w-9 items-center justify-center rounded-md bg-yellow-400 text-gray-900 shadow-sm transition-colors hover:bg-yellow-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-300 focus-visible:ring-offset-2`

  return (
    <>
    <AdminLayout>
      <div className={`${handles.adminCoupons} min-h-screen bg-gray-50`}>
        <div className="mx-auto max-w-7xl space-y-8 px-4 py-8">
          <div className={`${handles.couponsHeader}`}>
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h1 className={`${handles.couponsTitle} text-3xl font-bold text-gray-900`}>
                  Gestão de Cupons
                </h1>
                <p className={`${handles.couponsSubtitle} mt-1 text-base text-gray-600`}>
                  Gerencie cupons de desconto e promoções com consistência
                </p>
              </div>
              <button
                onClick={handleCreateCoupon}
                className={`${handles.couponsActionButton} inline-flex items-center justify-center rounded-lg bg-blue-600 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2`}
              >
                + Novo Cupom
              </button>
            </div>
          </div>

          <section
            className={`${handles.couponsStats} grid gap-6 md:grid-cols-3`}
            aria-label="Resumo de cupons"
          >
            <article
              className={`${handles.couponsStatCard} rounded-xl border border-gray-100 bg-white p-6 shadow-sm`}
            >
              <p className={`${handles.couponsStatValue} text-3xl font-bold text-blue-700`}>
                {stats.total}
              </p>
              <p className={`${handles.couponsStatLabel} mt-2 text-sm text-gray-600`}>
                Total de cupons cadastrados
              </p>
            </article>
            <article
              className={`${handles.couponsStatCard} rounded-xl border border-gray-100 bg-white p-6 shadow-sm`}
            >
              <p className={`${handles.couponsStatValue} text-3xl font-bold text-green-600`}>
                {stats.active}
              </p>
              <p className={`${handles.couponsStatLabel} mt-2 text-sm text-gray-600`}>
                Cupons ativos
              </p>
            </article>
            <article
              className={`${handles.couponsStatCard} rounded-xl border border-gray-100 bg-white p-6 shadow-sm`}
            >
              <p className={`${handles.couponsStatValue} text-3xl font-bold text-yellow-500`}>
                {stats.usages}
              </p>
              <p className={`${handles.couponsStatLabel} mt-2 text-sm text-gray-600`}>
                Utilizações registradas
              </p>
            </article>
          </section>

          <section
            className={`${handles.couponsTableWrapper} rounded-lg bg-white shadow-sm`}
            aria-label="Lista de cupons"
          >
            <div className="overflow-x-auto">
              <table className={`${handles.couponsTable} w-full`}>
                <thead className={`${handles.tableHeader} bg-gray-50`}>
                  <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <th scope="col" className="px-6 py-3">
                      Código
                    </th>
                    <th scope="col" className="px-6 py-3">
                      Tipo
                    </th>
                    <th scope="col" className="px-6 py-3">
                      Valor
                    </th>
                    <th scope="col" className="px-6 py-3">
                      Período
                    </th>
                    <th scope="col" className="px-6 py-3">
                      Usos
                    </th>
                    <th scope="col" className="px-6 py-3">
                      Status
                    </th>
                    <th
                      scope="col"
                      className="sticky right-0 z-20 bg-gray-50 px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 shadow-[inset_1px_0_0_0_rgb(226,232,240)]"
                    >
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {isLoading ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="px-6 py-12 text-center text-sm text-gray-500"
                      >
                        Carregando cupons...
                      </td>
                    </tr>
                  ) : coupons.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="px-6 py-12 text-center text-sm text-gray-500"
                      >
                        Nenhum cupom cadastrado ainda. Clique em "+ Novo Cupom" para criar.
                      </td>
                    </tr>
                  ) : (
                    coupons.map((coupon) => {
                      const typeConfig = getTypeConfig(coupon.type)
                      const hasMaxUses = typeof coupon.maxUses === 'number' && coupon.maxUses > 0
                      const usageText = hasMaxUses
                        ? `${coupon.usedCount} de ${coupon.maxUses}`
                        : `${coupon.usedCount}`

                      return (
                        <tr
                          key={coupon.id}
                          className={`${handles.tableRow} transition-colors hover:bg-gray-50`}
                        >
                          <td className="whitespace-nowrap px-6 py-4">
                            <div>
                              <p
                                className={`${handles.couponCode} text-sm font-semibold text-gray-900`}
                              >
                                {coupon.code}
                              </p>
                              <p
                                className={`${handles.couponDescription} mt-1 max-w-md text-sm text-gray-500`}
                              >
                                {coupon.description}
                              </p>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4">
                            <span
                              className={`${handles.couponTypePill} inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${typeConfig.className}`}
                            >
                              {typeConfig.label}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4">
                            <p className={`${handles.couponValue} text-sm text-gray-900`}>
                              {coupon.type === 'percentage'
                                ? `${coupon.value}%`
                                : coupon.type === 'fixed'
                                ? formatCurrency(coupon.value)
                                : 'Frete grátis'}
                            </p>
                            {coupon.minOrderValue ? (
                              <p className="text-xs text-gray-500">
                                Mínimo de {formatCurrency(coupon.minOrderValue)}
                              </p>
                            ) : null}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">
                            <div>
                              <span>{formatDate(coupon.validFrom)}</span>
                              <span className="mx-1 text-gray-400" aria-hidden="true">
                                —
                              </span>
                              <span>{formatDate(coupon.validUntil)}</span>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4">
                            <p className={`${handles.couponUsage} text-sm text-gray-900`}>
                              {usageText}
                            </p>
                            {hasMaxUses ? (
                              <p className="text-xs text-gray-500">
                                Restam {Math.max((coupon.maxUses ?? 0) - coupon.usedCount, 0)}
                              </p>
                            ) : (
                              <p className="text-xs text-gray-500">Sem limite definido</p>
                            )}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4">
                            <span
                              className={`${handles.couponStatus} inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                                coupon.isActive
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-700'
                              }`}
                            >
                              {coupon.isActive ? 'Ativo' : 'Inativo'}
                            </span>
                          </td>
                          <td className="sticky right-0 z-10 whitespace-nowrap bg-white px-6 py-4 shadow-[inset_1px_0_0_0_rgb(226,232,240)]">
                            <div className={`${handles.couponActions} flex items-center justify-end gap-2`}>
                              <button
                                onClick={() => handleViewCoupon(coupon)}
                                className={actionButtonClassName}
                                title="Visualizar cupom"
                                aria-label="Visualizar cupom"
                              >
                                <svg
                                  className="h-4 w-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={1.8}
                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                  />
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={1.8}
                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                  />
                                </svg>
                                <span className="sr-only">Visualizar cupom</span>
                              </button>
                              <button
                                onClick={() => handleEditCoupon(coupon)}
                                className={actionButtonClassName}
                                title="Editar cupom"
                                aria-label="Editar cupom"
                              >
                                <svg
                                  className="h-4 w-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={1.8}
                                    d="M16.862 3.487a2.1 2.1 0 112.971 2.97L7.5 18.79 3 21l2.21-4.5 11.652-12.013z"
                                  />
                                </svg>
                                <span className="sr-only">Editar cupom</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>

      {viewingCoupon && (
        <CouponDetailModal
          coupon={viewingCoupon.coupon}
          usageLog={viewingCoupon.usageLog}
          isLoading={isDetailLoading}
          onClose={handleCloseViewCoupon}
          onToggleStatus={() => handleToggleCouponStatus(viewingCoupon.coupon)}
          onDelete={() => handleDeleteCoupon(viewingCoupon.coupon)}
          onEdit={() => {
            handleCloseViewCoupon()
            handleEditCoupon(viewingCoupon.coupon)
          }}
        />
      )}
      <CouponModal
        coupon={editingCoupon}
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        onSave={handleSaveCoupon}
        isSaving={isSaving}
      />
    </AdminLayout>

    <ConfirmModal
      isOpen={deleteConfirmOpen}
      onClose={() => {
        setDeleteConfirmOpen(false)
        setCouponToDelete(null)
      }}
      onConfirm={confirmDeleteCoupon}
      title="Confirmar Exclusão"
      message={`Tem certeza que deseja excluir o cupom "${couponToDelete?.code}"? Esta ação não pode ser desfeita.`}
      confirmLabel="Excluir"
      cancelLabel="Cancelar"
      confirmButtonClassName="bg-red-600 hover:bg-red-700"
      isLoading={false}
    />
  </>
  )
}

interface CouponModalProps {
  coupon: Coupon | null
  isOpen: boolean
  onSave: (values: CouponFormValues) => Promise<void>
  onClose: () => void
  isSaving: boolean
}

interface CouponDetailModalProps {
  coupon: Coupon
  usageLog: CouponUsageLog[]
  isLoading: boolean
  onClose: () => void
  onToggleStatus: () => void
  onDelete: () => void
  onEdit: () => void
}

const CouponDetailModal: React.FC<CouponDetailModalProps> = ({
  coupon,
  usageLog,
  isLoading,
  onClose,
  onToggleStatus,
  onDelete,
  onEdit
}) => {
  const handles = useCssHandles(CSS_HANDLES)
  const logItems = usageLog ?? []
  const latestUsage = logItems.reduce<CouponUsageLog | null>((latest, current) => {
    if (!latest) {
      return current
    }
    return new Date(current.usedAt).getTime() > new Date(latest.usedAt).getTime() ? current : latest
  }, null)

  const statusBadgeClass = coupon.isActive
    ? 'bg-green-100 text-green-800'
    : 'bg-red-100 text-red-700'

  const statusLabel = coupon.isActive ? 'Ativo' : 'Inativo'

  const formatTypeLabel = (type: Coupon['type']) => {
    switch (type) {
      case 'percentage':
        return 'Percentual'
      case 'fixed':
        return 'Valor Fixo'
      case 'free_shipping':
        return 'Frete Grátis'
      default:
        return type
    }
  }

  const formatCouponValue = (couponItem: Coupon) => {
    if (couponItem.type === 'percentage') {
      return `${couponItem.value}%`
    }
    if (couponItem.type === 'fixed') {
      return formatCurrency(couponItem.value)
    }
    return 'Frete grátis'
  }

  const formatDateTime = (value: string) => {
    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) {
      return '—'
    }
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(parsed)
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Detalhes do cupom ${coupon.code}`}
      size="lg"
      overlayClassName={`${handles.viewModal} ${handles.modalOverlay}`}
      contentClassName={`${handles.viewModalContent} w-full max-w-3xl`}
      headerClassName={`${handles.modalHeader}`}
      titleClassName={`${handles.modalTitle} text-blue-700`}
      closeButtonClassName={`${handles.modalClose}`}
      bodyClassName={`${handles.modalBody}`}
      bodyStyle={{ padding: 0 }}
      footer={
        <div className={`${handles.viewModalActions} flex w-full items-center justify-between gap-3`}>
          <button
            type="button"
            onClick={onDelete}
            className="rounded-lg border border-red-200 px-4 py-2 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-200 focus-visible:ring-offset-2"
          >
            Excluir cupom
          </button>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-200 focus-visible:ring-offset-2"
            >
              Fechar
            </button>
            <button
              type="button"
              onClick={onEdit}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            >
              Editar cupom
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-6 p-6">
        <div className={`${handles.viewModalHeader} flex flex-col gap-4 md:flex-row md:items-start md:justify-between`}>
          <div>
            <p className="text-sm font-medium text-gray-500">Código</p>
            <h2 className="text-2xl font-bold text-gray-900">{coupon.code}</h2>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${statusBadgeClass}`}>
                {statusLabel}
              </span>
              <span className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700">
                {formatTypeLabel(coupon.type)}
              </span>
              <span className="inline-flex items-center rounded-full bg-yellow-100 px-3 py-1 text-xs font-semibold text-yellow-700">
                {formatCouponValue(coupon)}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={onToggleStatus}
            className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
              coupon.isActive
                ? 'bg-red-50 text-red-600 hover:bg-red-100 focus-visible:ring-red-200'
                : 'bg-green-50 text-green-700 hover:bg-green-100 focus-visible:ring-green-200'
            }`}
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.8}
                d="M12 5v7"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.8}
                d="M17 12.5a5 5 0 11-10 0V9a5 5 0 1110 0v3.5z"
              />
            </svg>
            {coupon.isActive ? 'Desativar' : 'Ativar'}
          </button>
        </div>

        <div className={`${handles.viewModalSummaryGrid} grid gap-4 md:grid-cols-2`}>
          <div className="rounded-lg border border-gray-100 bg-gray-50/60 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Período válido</p>
            <p className="mt-1 text-sm text-gray-900">
              {formatDate(coupon.validFrom)} — {formatDate(coupon.validUntil)}
            </p>
          </div>
          <div className="rounded-lg border border-gray-100 bg-gray-50/60 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Regras</p>
            <p className="mt-1 text-sm text-gray-900">
              Pedido mínimo:{' '}
              {coupon.minOrderValue ? formatCurrency(coupon.minOrderValue) : 'Não definido'}
            </p>
            {coupon.maxUses ? (
              <p className="text-sm text-gray-500">Limite: {coupon.maxUses} utilizações</p>
            ) : (
              <p className="text-sm text-gray-500">Sem limite de utilizações</p>
            )}
          </div>
          <div className="rounded-lg border border-gray-100 bg-gray-50/60 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Utilizações</p>
            <p className="mt-1 text-sm font-semibold text-gray-900">
              {coupon.usedCount}
              {coupon.maxUses ? ` / ${coupon.maxUses}` : ''}
            </p>
            <p className="text-sm text-gray-500">
              Última utilização: {latestUsage ? formatDateTime(latestUsage.usedAt) : '—'}
            </p>
          </div>
          <div className="rounded-lg border border-gray-100 bg-gray-50/60 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Descrição</p>
            <p className="mt-1 text-sm text-gray-900">{coupon.description}</p>
          </div>
        </div>

        <section className={`${handles.usageLogContainer}`} aria-label="Log de uso do cupom">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">Histórico de utilização</h3>
            <span className="text-xs text-gray-500">
              {usageLog.length} registro{usageLog.length === 1 ? '' : 's'}
            </span>
          </div>

      {isLoading ? (
        <div className="flex items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50 p-6 text-sm text-gray-500">
          Carregando histórico de utilização...
        </div>
      ) : logItems.length === 0 ? (
        <div className={`${handles.usageLogEmpty} flex items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50 p-6 text-sm text-gray-500`}>
          Nenhum uso registrado para este cupom até o momento.
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-100">
          <div className="overflow-x-auto">
            <table className={`${handles.usageLogTable} w-full text-left`}>
              <thead className={`${handles.usageLogHeader} bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500`}>
                <tr>
                  <th className="px-4 py-3">Cliente</th>
                  <th className="px-4 py-3">Pedido</th>
                  <th className="px-4 py-3">Data</th>
                  <th className="px-4 py-3 text-right">Desconto</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {logItems.map((log) => (
                  <tr key={log.id} className="text-sm text-gray-700">
                    <td className="px-4 py-3 font-medium text-gray-900">{log.customerName}</td>
                    <td className="px-4 py-3 text-blue-600">{log.orderId ?? '—'}</td>
                    <td className="px-4 py-3">{formatDateTime(log.usedAt)}</td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900">
                      {log.discountValue !== undefined
                        ? formatCurrency(log.discountValue)
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
        </section>
      </div>
    </Modal>
  )
}

const getInitialFormValues = (coupon: Coupon | null): CouponFormValues => ({
  code: coupon?.code ?? '',
  type: coupon?.type ?? 'percentage',
  value: coupon?.value ?? 0,
  minOrderValue: coupon?.minOrderValue ?? undefined,
  maxUses: coupon?.maxUses ?? undefined,
  validFrom: formatDateInputValue(coupon?.validFrom),
  validUntil: formatDateInputValue(coupon?.validUntil),
  isActive: coupon?.isActive ?? true,
  description: coupon?.description ?? ''
})

const CouponModal: React.FC<CouponModalProps> = ({
  coupon,
  isOpen,
  onClose,
  onSave,
  isSaving
}) => {
  const handles = useCssHandles(CSS_HANDLES)
  const formId = useId()
  
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors }
  } = useForm<CouponFormValues>({
    defaultValues: getInitialFormValues(coupon)
  })

  useEffect(() => {
    reset(getInitialFormValues(coupon))
  }, [coupon, reset])

  const onSubmit = async (data: CouponFormValues) => {
    await onSave(data)
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={coupon ? 'Editar cupom' : 'Novo cupom'}
      size="lg"
      overlayClassName={`${handles.modal} ${handles.modalOverlay}`}
      contentClassName={`${handles.modalContent} w-full max-w-2xl`}
      headerClassName={`${handles.modalHeader}`}
      titleClassName={`${handles.modalTitle} text-blue-700`}
      closeButtonClassName={`${handles.modalClose}`}
      bodyClassName={`${handles.modalBody}`}
      bodyStyle={{ padding: 0 }}
      footer={
        <div className={`${handles.modalFooter} flex w-full justify-end gap-3`}>
          <button
            type="button"
            onClick={onClose}
            className={`${handles.cancelButton} rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300 focus-visible:ring-offset-2`}
          >
            Cancelar
          </button>
          <button
            type="submit"
            form={formId}
            disabled={isSaving}
            className={`${handles.saveButton} rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70`}
          >
            {isSaving ? 'Salvando...' : coupon ? 'Salvar alterações' : 'Cadastrar cupom'}
          </button>
        </div>
      }
    >
      <form id={formId} onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-6 p-6">
          <div className={`${handles.formRow} grid gap-4 md:grid-cols-2`}>
            <div className={`${handles.formGroup}`}>
              <label
                htmlFor="coupon-code"
                className={`${handles.formLabel} mb-2 block text-sm font-medium text-gray-700`}
              >
                Código do cupom *
              </label>
              <Input
                id="coupon-code"
                type="text"
                {...register('code', {
                  required: 'Código do cupom é obrigatório'
                })}
                className={`${handles.formInput} ${errors.code ? 'border-red-300' : ''}`}
                error={!!errors.code}
                helperText={errors.code?.message}
              />
            </div>
            <div className={`${handles.formGroup}`}>
              <label
                htmlFor="coupon-type"
                className={`${handles.formLabel} mb-2 block text-sm font-medium text-gray-700`}
              >
                Tipo *
              </label>
              <Controller
                name="type"
                control={control}
                rules={{
                  required: 'Tipo é obrigatório'
                }}
                render={({ field }) => (
                  <>
                    <Select
                      id="coupon-type"
                      value={field.value}
                      onChange={(value) => field.onChange(value as Coupon['type'])}
                      options={[
                        { value: 'percentage', label: 'Percentual' },
                        { value: 'fixed', label: 'Valor Fixo' },
                        { value: 'free_shipping', label: 'Frete Grátis' }
                      ]}
                      placeholder="Selecione o tipo"
                      className={`${handles.formSelect}`}
                    />
                    {errors.type && (
                      <p className="mt-1 text-sm text-red-600">{errors.type.message}</p>
                    )}
                  </>
                )}
              />
            </div>
          </div>

          <div className={`${handles.formRow} grid gap-4 md:grid-cols-3`}>
            <div className={`${handles.formGroup}`}>
              <label
                htmlFor="coupon-value"
                className={`${handles.formLabel} mb-2 block text-sm font-medium text-gray-700`}
              >
                Valor *
              </label>
              <Input
                id="coupon-value"
                type="number"
                min={0}
                {...register('value', {
                  required: 'Valor é obrigatório',
                  valueAsNumber: true,
                  min: { value: 0, message: 'Valor deve ser maior ou igual a 0' }
                })}
                className={`${handles.formInput} ${errors.value ? 'border-red-300' : ''}`}
                error={!!errors.value}
                helperText={errors.value?.message}
              />
            </div>
            <div className={`${handles.formGroup}`}>
              <label
                htmlFor="coupon-min-order"
                className={`${handles.formLabel} mb-2 block text-sm font-medium text-gray-700`}
              >
                Pedido mínimo
              </label>
              <Controller
                name="minOrderValue"
                control={control}
                render={({ field }) => (
                  <Input
                    id="coupon-min-order"
                    type="number"
                    min={0}
                    value={field.value ?? undefined}
                    onChange={(e) => {
                      const value = e.target.value
                      field.onChange(value ? Number(value) : undefined)
                    }}
                    className={handles.formInput}
                  />
                )}
              />
            </div>
            <div className={`${handles.formGroup}`}>
              <label
                htmlFor="coupon-max-uses"
                className={`${handles.formLabel} mb-2 block text-sm font-medium text-gray-700`}
              >
                Máximo de usos
              </label>
              <Controller
                name="maxUses"
                control={control}
                render={({ field }) => (
                  <Input
                    id="coupon-max-uses"
                    type="number"
                    min={0}
                    value={field.value ?? undefined}
                    onChange={(e) => {
                      const value = e.target.value
                      field.onChange(value ? Number(value) : undefined)
                    }}
                    className={handles.formInput}
                  />
                )}
              />
            </div>
          </div>

          <div className={`${handles.formRow} grid gap-4 md:grid-cols-2`}>
            <div className={`${handles.formGroup}`}>
              <label
                htmlFor="coupon-valid-from"
                className={`${handles.formLabel} mb-2 block text-sm font-medium text-gray-700`}
              >
                Válido de *
              </label>
              <Controller
                name="validFrom"
                control={control}
                rules={{ required: 'Data inicial é obrigatória' }}
                render={({ field }) => (
                  <Input
                    id="coupon-valid-from"
                    type="date"
                    value={field.value || ''}
                    onChange={(event) => field.onChange(event.target.value)}
                    className={`w-full ${handles.formInput} ${errors.validFrom ? 'p-invalid' : ''}`}
                  />
                )}
              />
              {errors.validFrom && (
                <p className="mt-1 text-xs text-red-600">{errors.validFrom.message as string}</p>
              )}
            </div>
            <div className={`${handles.formGroup}`}>
              <label
                htmlFor="coupon-valid-until"
                className={`${handles.formLabel} mb-2 block text-sm font-medium text-gray-700`}
              >
                Válido até *
              </label>
              <Controller
                name="validUntil"
                control={control}
                rules={{ required: 'Data final é obrigatória' }}
                render={({ field }) => (
                  <Input
                    id="coupon-valid-until"
                    type="date"
                    value={field.value || ''}
                    onChange={(event) => field.onChange(event.target.value)}
                    className={`w-full ${handles.formInput} ${errors.validUntil ? 'p-invalid' : ''}`}
                  />
                )}
              />
              {errors.validUntil && (
                <p className="mt-1 text-xs text-red-600">{errors.validUntil.message as string}</p>
              )}
            </div>
          </div>

          <div className={`${handles.formGroup}`}>
            <label
              htmlFor="coupon-description"
              className={`${handles.formLabel} mb-2 block text-sm font-medium text-gray-700`}
            >
              Descrição *
            </label>
            <Textarea
              id="coupon-description"
              {...register('description', {
                required: 'Descrição é obrigatória'
              })}
              rows={4}
              className={`${handles.formTextarea} w-full rounded-lg border px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.description ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
            )}
          </div>

          <div className="flex items-center gap-3">
            <Checkbox
              id="coupon-active"
              {...register('isActive')}
            />
            <label htmlFor="coupon-active" className="text-sm font-medium text-gray-700">
              Cupom ativo
            </label>
          </div>
        </div>
      </form>
    </Modal>
  )
}

export default AdminCoupons

